import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { Fonts, Spacings } from '@styles/index';
import H5 from '@base/H5/H5';
import P1 from '@base/P1/P1';
import { PRACTICE_TYPE } from '@cont/Practice/constants';
import hexRgb from 'hex-rgb';
import { IChapterInfo } from 'types/syllabus';
import LessonType from '@cont/Syllabus/constants';
import { getIsLoggedIn } from '@selectors/auth';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { getIsGTPGoal } from '@api/selectors/constants';
import { useIsMobile } from '@stores/AppCommonData';
import useConstants from '@api/hooks/useConstants';
import useIsRecordedGoal from '@hooks/useIsRecordedGoal';
import usePracticeStore from '@stores/practice';
import NotesCard from '../NotesCard';
import PracticeCard from '../PracticeCard';
import Lesson from './Lesson';
import LessonMenu, { ILessonMenuPopupFn } from './LessonMenuPopup';
import StartPracticeDialog from '../StartPracticeDialog';

interface IIndexProps {
  bgColor: string;
  color: string;
}

interface IMarginProps {
  $marginBottom: string | number;
}

export interface ISyllabusChapterInfoProps {
  info: IChapterInfo;
  index: number;
  showNotes: boolean;
  topicTitle: string;
  topicUID: string;
  goalUID: string;
  color: string;
  missingConceptText: string;
  isSyllabusPageEducatorSpecific: boolean;
  disableTopicPage: (value: boolean) => void;
}

export const hexToRgb = (hex) => {
  const result = hexRgb(hex);
  return result
    ? `rgba(${result.red}, ${result.green}, ${result.blue}, 0.1)`
    : hex;
};

const Heading = styled.div<IMarginProps>`
  display: flex;
  align-items: center;
  margin-bottom: ${({ $marginBottom }) => $marginBottom};
`;

const Index = styled.div<IIndexProps>`
  background-color: ${({ bgColor }) => bgColor};
  color: ${({ color }) => color};
  border-radius: 50%;
  height: ${Spacings.SPACING_12B};
  width: ${Spacings.SPACING_12B};
  margin-right: ${Spacings.SPACING_4B};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.H4};
`;

const InnerWrapper = styled.div<IMarginProps>`
  display: flex;
  gap: ${Spacings.SPACING_4B};
  width: 100%;
  margin-bottom: ${({ $marginBottom }) => $marginBottom};
`;

const LessonsContainer = styled.div<IMarginProps>`
  margin-bottom: ${({ $marginBottom }) => $marginBottom};
`;

const StyP1 = styled(P1)`
  line-height: 18px;
`;

const LessonsWrapper = styled.div`
  margin-top: ${Spacings.SPACING_6B};
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_6B};
`;

const StyleP1 = styled(P1)`
  font-weight: ${Fonts.LIGHT};
  line-height: 18px;
  margin-top: ${Spacings.SPACING_1B};
`;

const StyH5 = styled(H5)`
  line-height: ${Spacings.SPACING_6B};
`;

const ChapterInfo: React.FC<ISyllabusChapterInfoProps> = ({
  info,
  index,
  showNotes,
  topicTitle,
  topicUID,
  goalUID,
  color,
  missingConceptText,
  isSyllabusPageEducatorSpecific,
  disableTopicPage
}) => {
  const popupRef = React.useRef<ILessonMenuPopupFn>();
  const {
    title,
    hasNotes,
    hasPractice,
    items = [],
    uid,
    level,
    educatorCoveredConcept = true
  } = info;
  const lightColor = hexToRgb(color);
  const areNotesVisible = showNotes && hasNotes;
  const isRecordedGoal = useIsRecordedGoal(goalUID);
  const isGTP = useConstants(getIsGTPGoal(goalUID));
  const isMobile = useIsMobile();
  const isLoggedIn = useSelector(getIsLoggedIn);
  const dispatch = useDispatch();
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  // @ts-ignore
  const { setPracticeSelectedTopic } = usePracticeStore();

  const [lessons, setLessons] = useState([]);
  const [reviewLessons, setReviewLessons] = useState([]);

  useEffect(() => {
    if (items.length) {
      const updatedLessons = [];
      const updatedReviewLessons = [];
      items.forEach((item) => {
        if (item.tags.find((tag) => tag === LessonType.REVIEW_LESSON))
          updatedReviewLessons.push(item);
        else updatedLessons.push(item);
      });
      setLessons(updatedLessons);
      setReviewLessons(updatedReviewLessons);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const handleShowPopup = (lesson) => (event) => {
    popupRef.current?.handleShowPopup(event, lesson);
  };

  const [dialogState, setDialogState] = useState({
    isOpen: false,
    title: ''
  });
  const onDialogClose = () => {
    setPracticeSelectedTopic({});
    setDialogState({ ...dialogState, isOpen: false, title: '' });
  };
  const onPracticeStart = (title) => {
    if (isLoggedIn) {
      setPracticeSelectedTopic({
        name: title,
        parentName: topicTitle,
        parentUID: topicUID,
        type: PRACTICE_TYPE.EACH_TOPIC,
        level,
        uid
      });
      setDialogState({ isOpen: true, title });
    } else {
      openLoginDrawer();
    }
  };

  return (
    <>
      <Heading $marginBottom={educatorCoveredConcept ? Spacings.SPACING_6B : 0}>
        <Index
          bgColor={
            educatorCoveredConcept
              ? lightColor
              : 'var(--color-base-light-0-secondary)'
          }
          color={educatorCoveredConcept ? color : 'var(--color-text-secondary)'}
          data-testid="index"
        >
          {index}
        </Index>
        <div>
          <StyH5 data-testid="concept-title">{title}</StyH5>
          {isSyllabusPageEducatorSpecific &&
            !educatorCoveredConcept &&
            missingConceptText && (
              <StyleP1
                color="var(--color-text-secondary)"
                data-testid="missing-concept-text"
              >
                {missingConceptText}
              </StyleP1>
            )}
        </div>
      </Heading>
      {/* 
        Notes and Practice touchpoints are not shown if syllabus page is educator specific.
      */}
      {!isSyllabusPageEducatorSpecific && (areNotesVisible || hasPractice) && (
        <InnerWrapper
          $marginBottom={
            lessons.length || reviewLessons.length ? Spacings.SPACING_6B : 0
          }
        >
          <>
            {areNotesVisible && <NotesCard />}
            {hasPractice && !isMobile && (
              <PracticeCard onPracticeStart={() => onPracticeStart(title)} />
            )}
          </>
        </InnerWrapper>
      )}
      {educatorCoveredConcept && (
        <>
          {reviewLessons.length > 0 && (
            <LessonsContainer
              $marginBottom={lessons.length ? Spacings.SPACING_6B : 0}
            >
              <StyP1 color="var(--color-text-secondary)">Review videos</StyP1>
              <LessonMenu ref={popupRef}>
                <LessonsWrapper>
                  {reviewLessons.map((item) => (
                    <Lesson
                      key={item.uid}
                      lessonInfo={item}
                      conceptInfo={info}
                      color={color}
                      lightColor={lightColor}
                      onClick={handleShowPopup(item)}
                      disableTopicPage={disableTopicPage}
                      isSyllabusPageEducatorSpecific={
                        isSyllabusPageEducatorSpecific
                      }
                      isRecordedGoal={isRecordedGoal}
                    />
                  ))}
                </LessonsWrapper>
              </LessonMenu>
            </LessonsContainer>
          )}
          {lessons.length > 0 && (
            <>
              <StyP1 color="var(--color-text-secondary)">
                {isGTP ? 'Comprehensive videos' : 'Lessons'}
              </StyP1>
              <LessonMenu ref={popupRef}>
                <LessonsWrapper>
                  {lessons.map((item) => (
                    <Lesson
                      key={item.uid}
                      lessonInfo={item}
                      conceptInfo={info}
                      color={color}
                      lightColor={lightColor}
                      onClick={handleShowPopup(item)}
                      disableTopicPage={disableTopicPage}
                      isSyllabusPageEducatorSpecific={
                        isSyllabusPageEducatorSpecific
                      }
                      isRecordedGoal={isRecordedGoal}
                    />
                  ))}
                </LessonsWrapper>
              </LessonMenu>
            </>
          )}
        </>
      )}
      <StartPracticeDialog
        dialogState={dialogState}
        onDialogClose={onDialogClose}
      />
    </>
  );
};

export default ChapterInfo;
